import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { Shield, FileText, Mail, Scale } from 'lucide-react';

export default function LegalPage() {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="legal-page">
      <SEO
        title={isFr ? 'Mentions Légales & CGV' : 'Legal Notice & Terms'}
        description={isFr ? "Mentions légales, conditions générales de vente et politique de confidentialité d'IA Academy." : "Legal notice, terms of service and privacy policy for IA Academy."}
        path="/legal"
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Poppins' }}>
          {isFr ? 'Mentions Légales & CGV' : 'Legal Notice & Terms'}
        </h1>

        {/* Mentions légales */}
        <Section icon={FileText} title={isFr ? '1. Mentions Légales' : '1. Legal Notice'}>
          <p><strong>{isFr ? 'Éditeur du site' : 'Site Publisher'}:</strong> IA Academy</p>
          <p><strong>{isFr ? 'Site internet' : 'Website'}:</strong> ia-academy.ai</p>
          <p><strong>Email:</strong> contact@ia-academy.ai</p>
          <p><strong>{isFr ? 'Hébergement' : 'Hosting'}:</strong> Emergent Labs</p>
          <p className="mt-3 text-sm text-slate-500">
            {isFr
              ? "Conformément aux dispositions des articles 6-III et 19 de la Loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'économie numérique (LCEN)."
              : "In accordance with the provisions of the French Law No. 2004-575 of June 21, 2004."}
          </p>
        </Section>

        {/* CGV */}
        <Section icon={Scale} title={isFr ? '2. Conditions Générales de Vente' : '2. Terms of Sale'}>
          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '2.1 Objet' : '2.1 Purpose'}</h4>
          <p>{isFr
            ? "Les présentes CGV régissent les ventes de formations en ligne proposées par IA Academy. Tout achat implique l'acceptation sans réserve des présentes conditions."
            : "These terms govern the sales of online training offered by IA Academy. Any purchase implies unreserved acceptance of these conditions."}</p>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '2.2 Prix et Paiement' : '2.2 Pricing & Payment'}</h4>
          <p>{isFr
            ? "Les prix sont indiqués en euros TTC. Le paiement s'effectue en ligne par carte bancaire via Stripe, plateforme de paiement sécurisée (PCI DSS)."
            : "Prices are shown in EUR including tax. Payment is made online by credit card via Stripe, a secure payment platform (PCI DSS compliant)."}</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>{isFr ? 'Plan Gratuit : 0€ — 1 heure de formation' : 'Free Plan: €0 — 1 hour of training'}</li>
            <li>{isFr ? 'Plan Standard : 49€ — 2 heures de formation' : 'Standard Plan: €49 — 2 hours of training'}</li>
            <li>{isFr ? 'Plan Premium : 129€/mois — Accès complet' : 'Premium Plan: €129/month — Full access'}</li>
            <li>{isFr ? 'Plan IA Expert : Sur devis (à partir de 200€)' : 'IA Expert Plan: Custom quote (from €200)'}</li>
          </ul>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '2.3 Droit de Rétractation' : '2.3 Withdrawal Right'}</h4>
          <p>{isFr
            ? "Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux contenus numériques fournis sur un support immatériel dont l'exécution a commencé. Toutefois, IA Academy offre un remboursement sous 7 jours si la formation n'a pas été commencée."
            : "In accordance with Article L221-28 of the French Consumer Code, the right of withdrawal does not apply to digital content provided on a non-tangible medium whose execution has begun. However, IA Academy offers a 7-day refund if the training has not been started."}</p>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '2.4 Accès aux Formations' : '2.4 Access to Training'}</h4>
          <p>{isFr
            ? "L'accès aux formations est ouvert immédiatement après confirmation du paiement. Les abonnements IA Fondations (19€/mois), IA Pro (49€/mois) et IA Expert (99€/personne/mois) sont valables 30 jours. Le plan IA Starter est gratuit et valable 7 jours."
            : "Access to training is granted immediately after payment confirmation. IA Fondations (€19/mo), IA Pro (€49/mo) and IA Expert (€99/person/mo) subscriptions are valid for 30 days. The IA Starter plan is free and valid for 7 days."}</p>
        </Section>

        {/* RGPD */}
        <Section icon={Shield} title={isFr ? '3. Politique de Confidentialité (RGPD)' : '3. Privacy Policy (GDPR)'}>
          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '3.1 Données Collectées' : '3.1 Data Collected'}</h4>
          <p>{isFr ? 'Nous collectons les données suivantes :' : 'We collect the following data:'}</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>{isFr ? "Nom, email (lors de l'inscription)" : 'Name, email (during registration)'}</li>
            <li>{isFr ? "Progression dans les formations (modules, quiz, certificats)" : 'Training progress (modules, quizzes, certificates)'}</li>
            <li>{isFr ? "Données de paiement (traitées exclusivement par Stripe)" : 'Payment data (processed exclusively by Stripe)'}</li>
            <li>{isFr ? "Conversations avec l'assistant IA (pour améliorer le service)" : 'Conversations with AI assistant (to improve service)'}</li>
          </ul>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '3.2 Finalités' : '3.2 Purposes'}</h4>
          <p>{isFr
            ? "Vos données sont utilisées pour : fournir les formations, suivre votre progression, améliorer le service, et communiquer avec vous (si vous y consentez)."
            : "Your data is used to: provide training, track your progress, improve the service, and communicate with you (with your consent)."}</p>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '3.3 Vos Droits' : '3.3 Your Rights'}</h4>
          <p>{isFr
            ? "Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition concernant vos données personnelles. Pour exercer ces droits, contactez-nous à : contact@ia-academy.ai"
            : "Under GDPR, you have the right to access, rectify, erase, port and object to your personal data processing. To exercise these rights, contact us at: contact@ia-academy.ai"}</p>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '3.4 Conservation' : '3.4 Data Retention'}</h4>
          <p>{isFr
            ? "Vos données sont conservées pendant la durée de votre compte. En cas de suppression de compte, vos données sont effacées sous 30 jours."
            : "Your data is retained for the duration of your account. Upon account deletion, your data is erased within 30 days."}</p>

          <h4 className="font-semibold text-slate-800 mt-4 mb-2">{isFr ? '3.5 Sécurité' : '3.5 Security'}</h4>
          <p>{isFr
            ? "Nous utilisons le chiffrement SSL/TLS, des tokens JWT avec cookies HTTPOnly, la protection CSRF, et le stockage sécurisé des mots de passe (bcrypt). Les paiements sont traités par Stripe (certifié PCI DSS Niveau 1)."
            : "We use SSL/TLS encryption, JWT tokens with HTTPOnly cookies, CSRF protection, and secure password storage (bcrypt). Payments are processed by Stripe (PCI DSS Level 1 certified)."}</p>
        </Section>

        {/* Cookies */}
        <Section icon={Mail} title={isFr ? '4. Politique de Cookies' : '4. Cookie Policy'}>
          <p>{isFr ? "IA Academy utilise les cookies suivants :" : "IA Academy uses the following cookies:"}</p>
          <table className="w-full mt-3 text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-slate-700">Cookie</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">{isFr ? 'Type' : 'Type'}</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">{isFr ? 'Durée' : 'Duration'}</th>
                <th className="text-left px-3 py-2 font-medium text-slate-700">{isFr ? 'Finalité' : 'Purpose'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr><td className="px-3 py-2 font-mono text-xs">refresh_token</td><td className="px-3 py-2">{isFr ? 'Essentiel' : 'Essential'}</td><td className="px-3 py-2">7 {isFr ? 'jours' : 'days'}</td><td className="px-3 py-2">{isFr ? 'Authentification sécurisée' : 'Secure authentication'}</td></tr>
              <tr><td className="px-3 py-2 font-mono text-xs">csrf_token</td><td className="px-3 py-2">{isFr ? 'Essentiel' : 'Essential'}</td><td className="px-3 py-2">7 {isFr ? 'jours' : 'days'}</td><td className="px-3 py-2">{isFr ? 'Protection CSRF' : 'CSRF protection'}</td></tr>
              <tr><td className="px-3 py-2 font-mono text-xs">session_token</td><td className="px-3 py-2">{isFr ? 'Essentiel' : 'Essential'}</td><td className="px-3 py-2">7 {isFr ? 'jours' : 'days'}</td><td className="px-3 py-2">{isFr ? 'Session Google OAuth' : 'Google OAuth session'}</td></tr>
            </tbody>
          </table>
          <p className="mt-3 text-sm text-slate-500">
            {isFr
              ? "Ces cookies sont strictement nécessaires au fonctionnement du site. Aucun cookie publicitaire ou de tracking n'est utilisé."
              : "These cookies are strictly necessary for the site to function. No advertising or tracking cookies are used."}
          </p>
        </Section>

        <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <p>{isFr ? 'Dernière mise à jour : Mars 2026' : 'Last updated: March 2026'}</p>
          <p>{isFr ? 'Contact : contact@ia-academy.ai' : 'Contact: contact@ia-academy.ai'}</p>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Poppins' }}>
        <Icon className="w-5 h-5 text-blue-600" />
        {title}
      </h2>
      <div className="text-sm text-slate-600 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}
